import {AllCriteria} from "@/types/Criteria.ts";
import {keyValueMapping, getCriteriaValue} from "@/utils/criteria-helper.tsx";
import type {Preference} from "@/types/Preference.ts";

// --- Slot config (broad: 3 slots, scores 4/2/1; specific: 4 slots, scores 4/2/1/1)

const BROAD_SLOTS = [
    {key: 'b1Key' as const, value: 'b1Value' as const, score: 4},
    {key: 'b2Key' as const, value: 'b2Value' as const, score: 2},
    {key: 'b3Key' as const, value: 'b3Value' as const, score: 1},
] as const;

const SPECIFIC_SLOTS = [
    {key: 's1Key' as const, value: 's1Value' as const},
    {key: 's2Key' as const, value: 's2Value' as const},
    {key: 's3Key' as const, value: 's3Value' as const},
    {key: 's4Key' as const, value: 's4Value' as const},
] as const;

const SPECIFIC_SCORE_PER_SLOT = [4, 2, 1, 1] as const;

/** Gender + sexual orientation shown as one row in specific criteria. */
const GENDER_ORIENTATION_PAIR: readonly ['genderId', 'sexualOrientationId'] = ['genderId', 'sexualOrientationId'];

export type BroadMatchItem = { key: string; value: string; match: boolean };
export type SpecificMatchItem = { keys: string[]; values: string[]; match: boolean };

export type GetMatchesResult = { broadMatched: BroadMatchItem[]; specificMatched: SpecificMatchItem[] };

type PreferenceSlots = Pick<Preference, 'b1Key' | 'b1Value' | 'b2Key' | 'b2Value' | 'b3Key' | 'b3Value' | 's1Key' | 's1Value' | 's2Key' | 's2Value' | 's3Key' | 's3Value' | 's4Key' | 's4Value'>;

type SpecificSlot = (typeof SPECIFIC_SLOTS)[number];

/**
 * Builds match items for broad criteria (b1–b3) and specific criteria (s1–s4),
 * using remaining brScore/spScore to mark each row as matched or not.
 */
export function getMatches(
    criteria: AllCriteria | undefined,
    brScore: number,
    spScore: number,
    preference: PreferenceSlots | null | undefined
): GetMatchesResult {
    if (!preference) {
        return { broadMatched: [], specificMatched: [] };
    }

    const broadMatched = buildBroadMatchItems(preference, criteria, brScore);
    const specificMatched = buildSpecificMatchItems(preference, criteria, spScore);

    return { broadMatched, specificMatched };
}

function buildBroadMatchItems(
    preference: PreferenceSlots,
    criteria: AllCriteria | undefined,
    brScore: number
): BroadMatchItem[] {
    const items: BroadMatchItem[] = [];
    let remainingScore = brScore;

    for (const slot of BROAD_SLOTS) {
        const preferenceKey = preference[slot.key];
        const mapping = preferenceKey ? keyValueMapping[preferenceKey] : undefined;
        const label = mapping?.label ?? '';
        const displayValue = preferenceKey
            ? getCriteriaValue(criteria, preferenceKey, preference[slot.value])
            : '';
        const match = remainingScore >= slot.score;
        if (match) remainingScore -= slot.score;

        items.push({ key: label, value: String(displayValue), match });
    }

    return items;
}

function buildSpecificMatchItems(
    preference: PreferenceSlots,
    criteria: AllCriteria | undefined,
    spScore: number
): SpecificMatchItem[] {
    const items: SpecificMatchItem[] = [];
    let remainingScore = spScore;

    for (let i = 0; i < SPECIFIC_SLOTS.length; i++) {
        const slot = SPECIFIC_SLOTS[i];
        const preferenceKey = preference[slot.key] as string | undefined;

        if (!preferenceKey) continue;

        const slotScore = SPECIFIC_SCORE_PER_SLOT[i];
        const match = remainingScore >= slotScore;
        const isGenderOrientationPair = preferenceKey === GENDER_ORIENTATION_PAIR[0];
        const nextSlot = isGenderOrientationPair ? SPECIFIC_SLOTS[i + 1] : undefined;

        const { keys, values } = isGenderOrientationPair && nextSlot && preference[nextSlot.key]
            ? buildGenderOrientationRow(preference, criteria, slot, nextSlot)
            : buildSingleCriterionRow(preference, criteria, preferenceKey, slot);

        if (match) remainingScore -= slotScore;
        items.push({ keys, values, match });

        if (isGenderOrientationPair && nextSlot) {
            i++; // skip next slot (already included in this row)
        }
    }

    return items;
}

function buildSingleCriterionRow(
    preference: PreferenceSlots,
    criteria: AllCriteria | undefined,
    preferenceKey: string,
    slot: SpecificSlot
): { keys: string[]; values: string[] } {
    const mapping = keyValueMapping[preferenceKey];
    const label = mapping?.label ?? preferenceKey;
    const value = getCriteriaValue(criteria, preferenceKey, preference[slot.value]);
    return {
        keys: [label],
        values: [String(value)],
    };
}

function buildGenderOrientationRow(
    preference: PreferenceSlots,
    criteria: AllCriteria | undefined,
    slot: SpecificSlot,
    nextSlot: SpecificSlot
): { keys: string[]; values: string[] } {
    const [genderKey, orientationKey] = GENDER_ORIENTATION_PAIR;
    const genderLabel = keyValueMapping[genderKey]?.label ?? genderKey;
    const orientationLabel = keyValueMapping[orientationKey]?.label ?? orientationKey;
    const genderValue = getCriteriaValue(criteria, genderKey, preference[slot.value]);
    const orientationValue = getCriteriaValue(criteria, orientationKey, preference[nextSlot.value]);

    return {
        keys: [genderLabel, orientationLabel],
        values: [String(genderValue), String(orientationValue)],
    };
}
